import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Wrapper } from '../../components/wrapper.component';
import { Program, TimeZoneDetails } from 'globals/store/programs.data';
import { getTimeZoneDetails } from '../../helpers/helpers';
import { JoiEvent, Venue } from 'globals/store/events.data';
import { formattedDateReselector, getProgramListOfTimeZonesUsed, programPermissionsReselector } from '../../store/reselectors/programs.reselector';
import { openDialogGetResult } from '../../helpers/angular-helpers';
import { VenueDialogData, VenueSelectDialogComponent } from '../program/venue-select.dialog.component';
import { CommonModule } from '@angular/common';
import { JoiInputComponent } from 'src/app/components/joi-input/joi-input.component';
import { AddManageWrapperComponent } from 'src/app/components/wrappers/add-manage-wrapper.component';
import { ToggleWrapperComponent } from 'src/app/components/wrappers/toggle-wrapper.component';
import { DatePickerComponent } from 'src/app/components/misc/date-picker.component';
import { TimeZonePickerComponent } from 'src/app/components/misc/timezone-picker.component';
import { date2str } from 'globals/helpers/helpers';

@Component({
    selector: 'program-basics',
    templateUrl: './program-basics.html',
    styleUrls: ['./program-basics.scss', '../manage/manage-program-setup.scss'],
    standalone: true,
    imports: [CommonModule, JoiInputComponent, AddManageWrapperComponent, ToggleWrapperComponent, DatePickerComponent, TimeZonePickerComponent],
})
export class ProgramBasicsComponent extends Wrapper<ProgramBasicsComponent> implements OnInit {
  _file = 'ProgramBasicsComponent'
  @Input() program: Program
  @Input() event: JoiEvent
  @Input() readOnly: boolean

  @Output() dateChanged: EventEmitter<void> = new EventEmitter()
  formatted_date: Date
  otherTimezones:TimeZoneDetails[] = []

  ngOnInit() {
    this.subProp(this.store.select(getProgramListOfTimeZonesUsed, this.program.id), 'otherTimezones')
    this.selectProp(formattedDateReselector, this.program.id, 'formatted_date') 
  }

  saveAssetIds(ids: string[]) {
    const oldIds = this.program.assetIds || []
    this.program.assetIds = [...oldIds, ...ids]
    this.saveProgram()
  }

  removeAssetId(idToRemove: string) {
    this.program.assetIds = this.program.assetIds.filter(id => id !== idToRemove)
    this.saveProgram()
  }

  async saveDate(new_date: Date) {
    this.dateChanged.emit()
    this.program.date = date2str(new Date(new_date));
    this.programService.storeProgramDates(true)
    this.updateUTCOffset(this.program.timeZoneDetails.timeZoneId, this.program.secondaryTimeZoneDetails?.timeZoneId, new_date ) // saves through this.  Don't want to pass full venue details object either
  }

  async saveProgram() {
    await this.programService.upsertProgram(this.program, {file:this._file, function:'saveProgram', data:{program: this.program}}, false)
  }


  toggleVenueConfirmed() {
    // add logic to confirm venue record in events
    this.program.venueConfirmed = !this.program.venueConfirmed
    this.saveProgram()
  } 

  toggleLocationConfirmed() {
    this.program.locationConfirmed = !this.program.locationConfirmed
    this.saveProgram()
  } 

  toggleDateConfirmed() {
    this.program.dateConfirmed = !this.program.dateConfirmed
    this.saveProgram()
  } 

  toggleProgramConfirmed() {
    this.program.programConfirmed = !this.program.programConfirmed
    this.saveProgram()
  }
  
  addDisplayTimeZone(){
    // switch program.timezone to secondary timezone
    this.program.secondaryTimeZoneDetails = { ...this.program.timeZoneDetails }
  }

  removeDisplayTimeZone() {
    if(!this.program.secondaryTimeZoneDetails) return;
    console.log("Removing display timezone")
    const { timeZoneId, offset, offsetText, abbr } = this.program.secondaryTimeZoneDetails;
    this.program.timeZoneDetails = { timeZoneId, offset, offsetText, abbr } as TimeZoneDetails
    this.program.secondaryTimeZoneDetails = null;
    this.saveProgram();
  }

  updateDisplayTimeZone(displayTimeZone: TimeZoneDetails) {
    const timezone = this.program.timeZoneDetails.timeZoneId;
    // console.log({ timezone, displayTimeZone, program: jsonCopyObject(this.program) })
    if(this.program.secondaryTimeZoneDetails && displayTimeZone.timeZoneId === this.program.secondaryTimeZoneDetails.timeZoneId){
      this.removeDisplayTimeZone();
    } else if(this.program.secondaryTimeZoneDetails || displayTimeZone.timeZoneId !== timezone) {
      if(!this.program.secondaryTimeZoneDetails) this.addDisplayTimeZone();
      this.program.timeZoneDetails = displayTimeZone;
      this.saveProgram()
    }
  }

  
  async manageVenue(event: Event){
    const rect = (<HTMLElement>event.target).getBoundingClientRect()
    
    const top = Math.min(rect.bottom - 10, window.innerHeight - 410)
    const position =  {top, left: rect.left};

    const data: VenueDialogData = {
      position,
      venues: Object.values(this.event.venues).filter(v=>!!v.googleId),
      selectedVenueId: this.program.venueId || null,
      type: 'Venue',
      closeOnChoice: true,
      dialogRef: null
    }
    const returnData: { venue: Venue } = await openDialogGetResult(this.dialog, VenueSelectDialogComponent, { width: '300px', disableClose: true, data, backdropClass: 'clear' })
    if (!returnData) return
    const venue = returnData.venue
    // console.log({ venue, program: jsonCopyObject(this.program) })
    if (!venue || (this.program.secondaryTimeZoneDetails && this.program.timeZoneDetails.timeZoneId === venue.timeZoneId)) {
      this.program.secondaryTimeZoneDetails = null
    }

    this.program.venueId = venue ? venue.id : null
    const date = this.program.date ? new Date(this.program.date) : new Date()
    // console.log({ venue, program: jsonCopyObject(this.program )})
    if (!this.program.secondaryTimeZoneDetails && !venue) {
      this.saveProgram()
      return
    }
    this.updateUTCOffset(
      this.program.secondaryTimeZoneDetails ? this.program.timeZoneDetails.timeZoneId : venue.timeZoneId,
      this.program.secondaryTimeZoneDetails ? venue.timeZoneId : null, date
    )
  }

  async updateUTCOffset(timeZoneId: string, secondaryTimeZoneId: string, date: Date){
    // console.log({ timeZoneId, secondaryTimeZoneId })
    this.program.timeZoneDetails = getTimeZoneDetails(timeZoneId, date)
    if (secondaryTimeZoneId) this.program.secondaryTimeZoneDetails = getTimeZoneDetails(secondaryTimeZoneId, date)
    await this.saveProgram()
  }


  
}